import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Spinner } from '../../components/ui/spinner';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  adminOpinionsApi,
  type OpinionArticleAdminItem,
  type OpinionSourceItem,
  type OpinionSourceStatus,
  type OpinionSyncStatus,
} from '../../services/api';

const KIND_LABELS: Record<string, string> = {
  transcript: 'Transcript',
  qa_pairs: 'Q&A pairs',
  notes: 'Notes',
};

const STATUS_STYLES: Record<OpinionSourceStatus, string> = {
  pending_export: 'bg-slate-100 text-slate-600',
  pr_open: 'bg-blue-100 text-blue-800',
  merged: 'bg-green-100 text-green-800',
  export_failed: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<OpinionSourceStatus, string> = {
  pending_export: 'exporting…',
  pr_open: 'PR open',
  merged: 'merged',
  export_failed: 'export failed',
};

// Display-only fallback until the sync status reports the backend's
// configured OPINIONS_KB_REPO; authoritative links come from each source's
// pr_url.
const DEFAULT_KB_REPO_URL = 'https://github.com/RCM-Library/rcm-opinions-kb';

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// An export normally completes within seconds. A row still 'pending_export'
// after this long is stuck (the background task died without recording an
// error) — offer the Retry button; the backend accepts pending_export
// retries and serializes concurrent attempts.
const STUCK_EXPORT_MS = 2 * 60 * 1000;

function canRetryExport(source: OpinionSourceItem): boolean {
  if (source.status === 'export_failed') return true;
  if (source.status !== 'pending_export' || !source.created_at) return false;
  const created = new Date(source.created_at).getTime();
  return !Number.isNaN(created) && Date.now() - created > STUCK_EXPORT_MS;
}

function SourceStatusBadge({ status }: { status: OpinionSourceStatus }) {
  return (
    <span
      className={`inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium ${STATUS_STYLES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/**
 * Opinion Articles ingestion panel (git-backed LLM wiki —
 * dev/projects/opinion-articles/).
 *
 * Pasting a source stores an immutable mirror row and exports it to the
 * rcm-opinions-kb repository as a source/* pull request. The synthesis agent
 * works that PR; review and publishing happen on GitHub (merge = approval);
 * published articles sync back into the table below. There is deliberately
 * no edit, publish, or delete here — those are repository pull requests.
 */
export const OpinionSourcesSection: React.FC = () => {
  const [sources, setSources] = useState<OpinionSourceItem[]>([]);
  const [articles, setArticles] = useState<OpinionArticleAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newTitle, setNewTitle] = useState('');
  const [newKind, setNewKind] = useState('transcript');
  const [newNotes, setNewNotes] = useState('');
  const [newContent, setNewContent] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<OpinionSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchAll = useCallback(async () => {
    setError(null);
    // allSettled, not all: one failing call (e.g. sync status) must not blank
    // the whole panel when the sources/articles the admin can act on loaded
    // fine.
    const [sourceList, articleList, sync] = await Promise.allSettled([
      adminOpinionsApi.listSources({ limit: 50 }),
      adminOpinionsApi.listArticles({ limit: 50 }),
      adminOpinionsApi.getSyncStatus(),
    ]);
    if (sourceList.status === 'fulfilled') setSources(sourceList.value.items);
    if (articleList.status === 'fulfilled') setArticles(articleList.value.items);
    if (sync.status === 'fulfilled') setSyncStatus(sync.value);
    if (sourceList.status === 'rejected' || articleList.status === 'rejected') {
      setError('Failed to load opinion sources.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // Poll while an export is in flight (so the PR link appears on its own —
  // the export background task takes a few seconds) or while a sync pass is
  // running (so "Sync in progress…" resolves without a manual refresh).
  const hasPendingExport = sources.some((s) => s.status === 'pending_export');
  const syncRunning = syncStatus?.status === 'running';
  useEffect(() => {
    if (!hasPendingExport && !syncRunning) return;
    const t = setInterval(() => void fetchAll(), 5000);
    return () => clearInterval(t);
  }, [hasPendingExport, syncRunning, fetchAll]);

  const errorDetail = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

  const handleAdd = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    setAdding(true);
    setError(null);
    setNotice(null);
    try {
      await adminOpinionsApi.createSource({
        title: newTitle.trim(),
        content: newContent.trim(),
        source_kind: newKind,
        notes: newNotes.trim() || undefined,
      });
      setNewTitle('');
      setNewNotes('');
      setNewContent('');
      setNotice(
        'Source stored — a pull request is being opened in the knowledge-base repository. ' +
          'Sources are immutable; corrections go in as a new source.',
      );
      await fetchAll();
    } catch (e: unknown) {
      setError(errorDetail(e, 'Failed to store source.'));
    } finally {
      setAdding(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setError(null);
    setNotice(null);
    try {
      const result = await adminOpinionsApi.syncNow();
      setSyncStatus(result);
      setNotice(
        result.error
          ? `Sync finished with an error: ${result.error}`
          : 'Sync complete — merged knowledge-base pages are up to date.',
      );
      await fetchAll();
    } catch (e: unknown) {
      setError(errorDetail(e, 'Failed to sync from the knowledge-base repository.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleRetryExport = async (source: OpinionSourceItem) => {
    setBusyId(source.id);
    setError(null);
    setNotice(null);
    try {
      await adminOpinionsApi.exportSource(source.id);
      setNotice(`Retrying export of “${source.title}”…`);
      await fetchAll();
    } catch (e: unknown) {
      setError(errorDetail(e, 'Failed to retry the export.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="px-4 py-3 border-b flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-medium">Opinion Articles (LLM Wiki)</h2>
          <p className="text-xs text-muted-foreground">
            Paste a raw transcript or Q&A document. It is committed to the knowledge-base
            repository and a pull request opens; the synthesis agent drafts wiki updates there.
            Review the diff and merge on GitHub to publish — merged articles appear below.
          </p>
        </div>
        <div className="flex items-center gap-3 whitespace-nowrap">
          <a href="/opinions" className="text-xs text-primary underline">
            Browse KB
          </a>
          <a
            href={syncStatus?.repo ? `https://github.com/${syncStatus.repo}` : DEFAULT_KB_REPO_URL}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary underline"
          >
            KB repository ↗
          </a>
        </div>
      </div>

      <div className="px-4 py-3 space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {notice && (
          <Alert>
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        )}

        {/* New source form */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Source title (e.g. Expert Q&A 2026-09-25)"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="max-w-md"
            />
            <Select value={newKind} onValueChange={setNewKind}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Kind" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="transcript">Transcript</SelectItem>
                <SelectItem value="qa_pairs">Q&A pairs</SelectItem>
                <SelectItem value="notes">Notes</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Notes (optional)"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              className="max-w-xs"
            />
          </div>
          <Textarea
            placeholder="Paste the raw transcript or question/answer content here…"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            rows={6}
          />
          <Button size="sm" onClick={handleAdd} disabled={adding || !newTitle.trim() || !newContent.trim()}>
            {adding ? <Spinner size="sm" /> : 'Submit source'}
          </Button>
        </div>

        {/* Sources table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-2">Source</th>
                <th className="py-2 pr-2">Kind</th>
                <th className="py-2 pr-2">Size</th>
                <th className="py-2 pr-2">Added</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Pull request</th>
                <th className="py-2 pr-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted-foreground">
                    <Spinner size="sm" />
                  </td>
                </tr>
              ) : sources.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted-foreground">
                    No sources yet. Paste a transcript above to start the knowledge base.
                  </td>
                </tr>
              ) : (
                sources.map((source) => (
                  <tr key={source.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-2 max-w-[240px]">
                      <div className="font-medium truncate" title={source.title}>
                        {source.title}
                      </div>
                      {source.notes && (
                        <div className="text-xs text-muted-foreground truncate">{source.notes}</div>
                      )}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap">
                      {KIND_LABELS[source.source_kind] ?? source.source_kind}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap">
                      {(source.content_chars / 1000).toFixed(1)}k
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap">{formatDate(source.created_at)}</td>
                    <td className="py-2 pr-2">
                      <div className="space-y-0.5">
                        <SourceStatusBadge status={source.status} />
                        {source.status === 'export_failed' && source.error && (
                          <div
                            className="text-xs text-destructive max-w-[180px] truncate"
                            title={source.error}
                          >
                            {source.error}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap">
                      {source.pr_url ? (
                        <a
                          href={source.pr_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline"
                        >
                          #{source.pr_number}
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        {canRetryExport(source) && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === source.id}
                            onClick={() => handleRetryExport(source)}
                          >
                            Retry export
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Published articles (synced mirror of the repository) */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="text-sm font-medium">Published articles</h3>
            <div className="flex items-center gap-2">
              {syncStatus?.status === 'running' || syncing ? (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Spinner size="sm" /> Sync in progress…
                </span>
              ) : syncStatus?.error ? (
                <span
                  className="text-xs font-medium text-destructive max-w-[320px] truncate"
                  title={syncStatus.error}
                >
                  Sync error: {syncStatus.error}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {syncStatus?.last_synced_at
                    ? `Last synced ${formatDate(syncStatus.last_synced_at)}`
                    : 'Not synced yet'}
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={syncing || syncStatus?.status === 'running'}
                onClick={handleSyncNow}
              >
                {syncing ? <Spinner size="sm" /> : 'Sync now'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Synced from the repository on merge. To change or remove one, open a pull request
            against <code>knowledge/wiki/</code>.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-2">Title</th>
                  <th className="py-2 pr-2">Slug</th>
                  <th className="py-2 pr-2">Type</th>
                  <th className="py-2 pr-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {articles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-muted-foreground">
                      No published articles yet.
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => (
                    <tr key={article.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 max-w-[340px] truncate" title={article.title}>
                        {article.title}
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs">{article.slug}</td>
                      <td className="py-2 pr-2">{article.page_type}</td>
                      <td className="py-2 pr-2 whitespace-nowrap">
                        {formatDate(article.updated_at || article.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpinionSourcesSection;
